import type { Board } from '@atlassian/jira-navigation-types/src/types.tsx';
import { ProjectLocation, UserAccountLocation } from '../model/index.tsx';
import type { State } from './types.tsx';

export const getInitialBoardsProps = (state: State) => state.initialBoards;

export const getCurrentProjectKey = (state: State): string | undefined => state.currentProjectKey;
export const getBaseUrl = (state: State): string => state.baseUrl;
export const getBoardsInLocation = (state: State): Board[] =>
	getInitialBoardsProps(state).inLocation.values;
export const isBoardInLocation = (state: State, board: Board) =>
	getBoardsInLocation(state).includes(board);
export const getOnNavigateUrl = (state: State) => state.onNavigateUrl;
export const getOnBoardClick = (state: State) => state.onBoardClick;
export const getRecentBoards = (state: State) => getInitialBoardsProps(state).recent;
export const wereInitialBoardsFetched = (state: State) => getInitialBoardsProps(state).wereFetched;
export const isLoadingInitialBoards = (state: State) => getInitialBoardsProps(state).isInProgress;
export const getBoardOwnerAccountId = (state: State) => state.boardOwnerAccountId;

export const getSelectedBoard = (state: State) => state.currentBoard;
export const getBoardById = (state: State, id?: string): Board | undefined => {
	if (!id) {
		return undefined;
	}

	const test = (board: Board) => board.id === id;

	return (
		getBoardsInLocation(state).find(test) ||
		getRecentBoards(state).find(test) ||
		(state.searchResults &&
			(state.searchResults.inLocation.values.find(test) ||
				state.searchResults.notInLocation.values.find(test)))
	);
};

export const getLocationType = (state: State) =>
	getCurrentProjectKey(state) ? ProjectLocation : UserAccountLocation;

export const getLocationKey = (state: State) => {
	switch (getLocationType(state)) {
		case ProjectLocation:
			return getCurrentProjectKey(state);
		default:
			return getBoardOwnerAccountId(state);
	}
};

export const shouldLoadBoardOwnerAccountId = (state: State): boolean =>
	getLocationType(state) === UserAccountLocation && !getBoardOwnerAccountId(state);

export const getRecentlyClickedBoardId = (state: State) => state.recentlyClickedItem;
export const getRecentlyClickedBoard = (state: State) =>
	getBoardById(state, getRecentlyClickedBoardId(state));

export const getViewAllUrl = (state: State) => `${getBaseUrl(state)}/jira/boards`;

export const isSearchInProgress = (state: State) => state.isSearchInProgress;
export const getSearchValue = (state: State) => state.searchValue;
export const getSearchResults = (state: State) => state.searchResults;
export const isSearchUsed = (state: State) => !!state.searchValue;

export const shouldRetryLoadMore = (state: State) => state.shouldRetryLoadMore;
export const getShowMoreStartAt = (state: State) =>
	(isSearchUsed(state) ? getSearchResults(state) : getInitialBoardsProps(state)).inLocation.values
		.length;

export const isLoadingMore = (state: State) => state.isLoadMoreInProgress;
export const isLoading = (state: State) =>
	isLoadingInitialBoards(state) ||
	isSearchInProgress(state) ||
	(isLoadingMore(state) && shouldRetryLoadMore(state));

export const hasSearchError = (state: State) => state.hasSearchError;
export const hasInitialBoardsError = (state: State) => getInitialBoardsProps(state).hasError;
export const hasLoadMoreError = (state: State) =>
	!isLoadingMore(state) && shouldRetryLoadMore(state);
export const isError = (state: State) =>
	hasLoadMoreError(state) ||
	(isSearchUsed(state) ? hasSearchError(state) : hasInitialBoardsError(state));
